//LIBRARIES
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { localize } from 'react-localize-redux';
import styled from 'styled-components';
import MediaQuery from 'react-responsive';
import { MdDelete as DeleteIcon } from 'react-icons/md';
import { MdEdit as EditIcon } from 'react-icons/md';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import { CardText } from 'material-ui/Card';
import AddIcon from '@material-ui/icons/AddBox';
import get from 'lodash/get';

//HELPERS
import { drawerStyles } from 'helpers/StyleHelper';
import { getTranslatedString } from 'helpers/i18n';
import { groupedTableData } from 'helpers/tableHelpers';
import { serviceRequestOptions } from 'helpers/VerbiageHelper';
import { sizes } from 'helpers/MediaHelper';
import { hostIsBoardwalk, hostIsCapreit } from 'helpers/CurrentWindowLocation';

//COMPONENTS
import MobileNavContainer from 'components/ui/MobileNavDropdown/MobileNavContainer';
import MobileNavCurrentTab from 'components/ui/MobileNavDropdown/MobileNavCurrentTab';
import AntDrawer from 'components/ui/Drawer';

import TenantOverviewHeader from './TenantOverviewHeader';
import TenantDetails from './TenantDetails';
import PadDetailForm from './PadDetailForm';
import TenantActivity from './TenantActivity/TenantActivity';

import UpdatePasswordDialog from 'components/shared/UpdatePasswordDialog';
import { RenderIfToggleOn, RenderIfToggleOff } from 'components/shared/ToggleRenderer';
import DocumentUploader from 'components/shared/DocumentUploader';
import TenantResidentVerbiageHelper from 'components/shared/TenantResidentVerbiageHelper';
import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import Header from 'components/shared/Header';
import TabbedPanel from 'components/shared/TabbedPanel';
import FooterButtons from 'components/shared/FooterButtons';

import Documents from 'components/landlord_app/rent_roll/components/options_popover/Documents';
import PaymentsLedger from 'components/landlord_app/rent_roll/components/options_popover/PaymentsLedger';
import TenantDrawer from 'components/landlord_app/tenants/components/TenantDrawer';
import ApplicantDialog from 'components/landlord_app/applications/components/ApplicationOverview/ApplicantDialog';
import MaintenanceTicketsMobile from 'components/landlord_app/maintenance/components/MaintenanceTicketsMobile';

//ACTIONS
import { createOrUpdateTenant } from 'actions/landlordApplicationsActions';
import { updateTenantPassword, updateTenantPadDetail } from 'actions/landlordTenantsActions';

const StyledTransactionCard = styled(Card)`
  color: #424242 !important;
  font-family: 'Circular', sans-serif !important;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  margin-bottom: 15px;
`;

const PAD_DETAIL_FORM = 'pad_detail';

const PAD_DETAIL_TAB_INDEX = 5;

class TenantOverview extends Component {
  state = {
    showConfirmationDialog: false,
    showPapConfirmationDialog: false,
    tenantDialogOpen: false,
    documentIsOpen: false,
    passwordUpdateDialog: false,
    saveButtonActive: false,
    tabIndex: 0,
    padDetailsMonthlyAmount: undefined,
    padDetailsTrackPayMonthlyAmount: false,
  };

  setTabIndex = (newTabIndex) => {
    this.setState({ tabIndex: newTabIndex });
  };

  mobileNavTabFactory = (tabHeaders, tabBodies) => {
    // The TabbedTable uses two arrays of the header and body
    // The new MobileNavContainer takes a tab object
    return tabHeaders.map((header, index) => ({
      value: index,
      label: header,
      component: tabBodies[index],
    }));
  };

  toggleEditTenantDialog = () => {
    this.setState((prevState) => ({
      tenantDialogOpen: !prevState.tenantDialogOpen,
    }));
  };

  toggleConfirmationDialog = () => {
    this.setState({
      showConfirmationDialog: !this.state.showConfirmationDialog,
    });
  };

  toggleUpdatePasswordDialog = () => {
    this.setState({
      passwordUpdateDialog: !this.state.passwordUpdateDialog,
    });
  };

  togglePapConfirmationDialog = () => {
    this.setState({
      showPapConfirmationDialog: !this.state.showPapConfirmationDialog,
    });
  };

  padConfirmationClicked = () => {
    this.props.deletePadDetail();
    this.togglePapConfirmationDialog();
  };

  deleteTenant = async () => {
    await this.props.deleteTenant();
    this.toggleConfirmationDialog();
    this.props.toggleDrawer();
  };

  updatePassword = async (newPassword) => {
    await this.props.updateTenantPassword(this.props.tenantDetails.id, newPassword);
    this.toggleUpdatePasswordDialog();
  };

  render() {
    const tenantOverviewServiceRequestOptions = {
      serviceRequest: getTranslatedString('landlordPortal.tenantOverview.serviceRequest'),
      requestForMaintenance: getTranslatedString('landlordPortal.tenantOverview.serviceRequestRFM'),
    };

    let tabHeaders = [getTranslatedString('landlordPortal.tenantOverview.details')];

    const tabBodies = [this._renderDetailsBody()];
    const tabFooters = [this._renderCloseOnlyFooter()];

    if (this.props.userPermissions.maintenance) {
      tabHeaders.push(serviceRequestOptions(tenantOverviewServiceRequestOptions));
      tabBodies.push(this._renderTicketsBody());
      tabFooters.push(this._renderCloseOnlyFooter());
    }

    tabHeaders.push(getTranslatedString('landlordPortal.residents.tab.activity'));
    tabBodies.push(<TenantActivity />);
    tabFooters.push(this._renderCloseOnlyFooter());

    if (this.props.showTenantDocuments && !hostIsCapreit()) {
      tabHeaders.push(getTranslatedString('landlordPortal.tenantOverview.documents'));
      tabBodies.push(this._renderDocumentsBody());
      tabFooters.push(this._renderCloseOnlyFooter());
    }

    if (this.props.showTenantLedger && !hostIsBoardwalk() && !hostIsCapreit()) {
      tabHeaders.push(getTranslatedString('landlordPortal.tenantOverview.ledger'));
      tabBodies.push(this._renderLedgerBody());
      tabFooters.push(this._renderCloseOnlyFooter());
    }

    if (this.props.tenantDetails.autopay_active) {
      tabHeaders.push(getTranslatedString('landlordPortal.tenantOverview.pap'));
      tabBodies.push(this._renderPapBody());
      tabFooters.push(this._renderCloseOnlyFooter());
    }

    const tabs = this.mobileNavTabFactory(tabHeaders, tabBodies);

    return (
      <Fragment>
        <TenantOverviewHeader
          fullName={this.props.tenantDetails.full_name}
          status={this.props.tenantDetails.status}
          isPriorOrFutureTenant={this.props.tenantDetails.is_prior_or_future_tenant}
          handleClickBack={this.props.toggleDrawer}
        />
        <MediaQuery query={`(min-device-width: ${sizes.desktop}px)`}>
          <TabbedPanel
            tabHeaders={tabHeaders}
            tabBodies={tabBodies}
            tabFooters={tabFooters}
            tabIndex={(value) => this.setTabIndex(value)}
          />
        </MediaQuery>
        <MediaQuery query={`(max-device-width: ${sizes.mobile}px)`}>
          <MobileNavContainer
            tabIndex={this.state.tabIndex}
            tabs={tabs}
            onChange={this.setTabIndex}
          />
          <MobileNavCurrentTab tabIndex={this.state.tabIndex} tabs={tabs} />
        </MediaQuery>
      </Fragment>
    );
  }

  _renderDetailsBody = () => (
    <div style={drawerStyles.tabBodyWrapper}>
      <RenderIfToggleOff toggle="ui.residents.new_edit_drawer">
        <ApplicantDialog
          application={this.props.tenantDetails.application}
          applicant={this.props.tenantDetails}
          applicantDialogOpen={this.state.tenantDialogOpen}
          toggleApplicantDialog={this.toggleEditTenantDialog}
          callback={this.props.reloadTenant}
          applicantType="tenant"
          hideSalary
          overrideHideApplicationDetails
          createOrUpdateTenant={this.props.createOrUpdateTenant}
        />
      </RenderIfToggleOff>
      <RenderIfToggleOn toggle="ui.residents.new_edit_drawer">
        <AntDrawer
          visible={this.state.tenantDialogOpen}
          onClose={this.toggleEditTenantDialog}
          width={550}
          closable={false}
        >
          {this.state.tenantDialogOpen && (
            <TenantDrawer
              application={this.props.tenantDetails.application}
              applicant={this.props.tenantDetails}
              toggleTenantDrawer={this.toggleEditTenantDialog}
              callback={this.props.reloadTenant}
              applicantType="tenant"
              hideSalary
              overrideHideApplicationDetails
              createOrUpdateTenant={this.props.createOrUpdateTenant}
            />
          )}
        </AntDrawer>
      </RenderIfToggleOn>
      <TenantDetails
        tenantDetails={this.props.tenantDetails}
        inviteTenant={this.props.inviteTenant}
        optOutLead={this.props.optOutLead}
        optInLead={this.props.optInLead}
        toggleEditTenantDialog={this.toggleEditTenantDialog}
        openAnotherTenant={this.props.openAnotherTenant}
        goToChat={this.props.goToChat}
        goToLease={this.props.goToLease}
        showLeaseSection
        showChatButton={this.props.showMessaging}
        isUserAdmin={this.props.isUserAdmin}
        leadID={this.props.tenantDetails.lead_id}
      />
      <RenderIfToggleOn toggle="ui.residents_page.delete_resident" dontRenderIf={hostIsCapreit()}>
        {this._renderDeleteButton()}
      </RenderIfToggleOn>
      <RenderIfToggleOn toggle="ui.residents_page.update_password" dontRenderIf={hostIsCapreit()}>
        {this._renderPasswordUpdate()}
      </RenderIfToggleOn>
      <ConfirmationDialog
        showDialog={this.state.showConfirmationDialog}
        hideDialog={this.toggleConfirmationDialog}
        userConfirmVerbiage={getTranslatedString('landlordPortal.tenantOverview.delete')}
        userConfirmAction={this.deleteTenant}
        dialogTitle={getTranslatedString('landlordPortal.tenantOverview.areYouSure')}
        dialogBodyVerbiage={getTranslatedString('landlordPortal.tenantOverview.areYouSureBody')}
      />
      <UpdatePasswordDialog
        showDialog={this.state.passwordUpdateDialog}
        hideDialog={this.toggleUpdatePasswordDialog}
        updatePasswordAction={this.updatePassword}
      />
    </div>
  );

  _renderDeleteButton = () => {
    if (!this.props.isUserAdmin) return;
    if (hostIsBoardwalk()) return;
    return (
      <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={this.toggleConfirmationDialog}
          style={{ borderWidth: '2px' }}
        >
          {getTranslatedString('landlordPortal.tenantOverview.delete')}{' '}
          <DeleteIcon style={{ fontSize: '21px', marginLeft: '8px' }} />
        </Button>
      </div>
    );
  };

  _renderPasswordUpdate = () => {
    if (!this.props.isUserAdmin) return;
    if (this.props.tenantDetails.status !== 'active') return;
    return (
      <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={this.toggleUpdatePasswordDialog}
          style={{ borderWidth: '2px' }}
        >
          {getTranslatedString('landlordPortal.tenantUpdatePassword.updatePassword')}{' '}
          <EditIcon style={{ fontSize: '21px', marginLeft: '8px' }} />
        </Button>
      </div>
    );
  };

  _renderTicketsBody = () => {
    const { tenantDetails, updateTicket, goToTicket } = this.props;
    return (
      <div style={drawerStyles.tabBodyWrapper}>
        <StyledTransactionCard>
          <CardText style={{ padding: '0 15px', overflow: 'auto' }}>
            <MaintenanceTicketsMobile
              containerHeightOverride="100%"
              data={groupedTableData(get(tenantDetails, 'maintenance_tickets', []))}
              goToTicket={goToTicket}
              updateMaintenenceTicket={updateTicket}
              bypassReactVirtualized
            />
          </CardText>
        </StyledTransactionCard>
      </div>
    );
  };

  _renderDocumentsBody = () => (
    <div style={drawerStyles.tabBodyWrapper}>
      {/************ Documents **************/}
      <StyledTransactionCard>
        <CardText style={{ padding: '0' }}>
          <Header
            name={
              <span style={{ textTransform: 'uppercase !important' }}>
                <TenantResidentVerbiageHelper uppercase />{' '}
                {getTranslatedString('landlordPortal.tenantOverview.documentsUpcase')}
              </span>
            }
          />
          <div className="row" style={{ padding: '15px' }}>
            <Documents />
          </div>
        </CardText>
      </StyledTransactionCard>
      <div className="row" style={{ textAlign: 'center' }}>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => this.props.toggleAddDocumentDialog(!this.props.documentIsOpen)}
        >
          {getTranslatedString('landlordPortal.tenantOverview.addDocument')}{' '}
          <AddIcon style={{ fontSize: '21px', marginLeft: '8px' }} />
        </Button>
      </div>
      <DocumentUploader
        dialogOpen={this.props.documentIsOpen}
        toggleDialog={() => this.props.toggleAddDocumentDialog(!this.props.documentIsOpen)}
        handleSubmit={this.props.addTenantDocument}
      />
    </div>
  );

  _renderLedgerBody = () => (
    <div style={drawerStyles.tabBodyWrapper}>
      <StyledTransactionCard>
        <CardText style={{ padding: '0' }}>
          <PaymentsLedger />
        </CardText>
      </StyledTransactionCard>
    </div>
  );

  _renderPapBody = () => (
    <div style={drawerStyles.tabBodyWrapper}>
      <StyledTransactionCard>
        <PadDetailForm
          formName={PAD_DETAIL_FORM}
          isSaveDisable={(authorizedAmount, trackPayAmout) =>
            this.updatePadDetailsField(authorizedAmount, trackPayAmout)
          }
          formData={this.props.padDetail}
          tenantDetails={this.props.tenantDetails}
        />
      </StyledTransactionCard>
      <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={this.togglePapConfirmationDialog}
          style={{ borderWidth: '2px' }}
        >
          {getTranslatedString('landlordPortal.tenantOverview.delete')}{' '}
          <DeleteIcon style={{ fontSize: '21px', marginLeft: '8px' }} />
        </Button>
      </div>
      <ConfirmationDialog
        showDialog={this.state.showPapConfirmationDialog}
        hideDialog={this.togglePapConfirmationDialog}
        userConfirmVerbiage={getTranslatedString('landlordPortal.tenantOverview.delete')}
        userConfirmAction={this.padConfirmationClicked}
        dialogTitle={getTranslatedString('landlordPortal.tenantOverview.areYouSure')}
        dialogBodyVerbiage={getTranslatedString('landlordPortal.tenantOverview.thisWillDeletePap')}
      />
    </div>
  );

  updatePadDetailsField = (authorizedAmount, trackPayMonthlyBalance) => {
    if (!authorizedAmount && !trackPayMonthlyBalance) {
      this.setState({
        saveButtonActive: false,
        padDetailsAuthorizedAmount: null,
        padDetailsTrackPayMonthlyAmount: false,
      });
    } else if (authorizedAmount || trackPayMonthlyBalance) {
      if (!this.state.saveButtonActive) this.setState({ saveButtonActive: true });
      this.setState({
        padDetailsAuthorizedAmount: authorizedAmount,
        padDetailsTrackPayMonthlyAmount: trackPayMonthlyBalance,
      });
    }
  };

  _renderCloseOnlyFooter = () => (
    <FooterButtons
      hideSubmitButton={this.state.tabIndex !== PAD_DETAIL_TAB_INDEX}
      disableSubmitOverride={this.state.saveButtonActive}
      submitAction={() => {
        const authorizedAmount = this.state.padDetailsAuthorizedAmount
          ? this.state.padDetailsAuthorizedAmount
          : 0;
        const payTotalAmount = this.state.padDetailsTrackPayMonthlyAmount;

        this.props.updateTenantPadDetail(
          authorizedAmount,
          payTotalAmount,
          this.props.tenantDetails.id
        );
      }}
      submitName={getTranslatedString('landlordPortal.tenantOverview.saveChanges')}
      backName={getTranslatedString('landlordPortal.tenantOverview.close')}
      backAction={this.props.toggleDrawer}
      position={'static'}
    />
  );
}

export default connect(
  (store) => {
    return {
      showTenantDocuments: store.userTypes.showTenantDocuments,
      showTenantLedger: store.userTypes.showTenantLedger,
      showMessaging: store.userTypes.showMessaging,
      isUserAdmin: store.userTypes.admin,
      userPermissions: store.userTypes.permissions,
    };
  },
  { createOrUpdateTenant, updateTenantPassword, updateTenantPadDetail }
)(localize()(TenantOverview));
